import { Head, Link, router } from "@inertiajs/react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { SearchableSelect } from "@/components/searchable-select";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDataTable } from "@/hooks/use-data-table";
import { useSyncedDebouncedValue } from "@/hooks/use-synced-debounced-value";
import AppLayout from "@/layouts/app-layout";
import { newSupplierPath, suppliersPath } from "@/routes";
import type {
  BreadcrumbItem,
  PagyMetadata,
  RansackFilters,
  Supplier,
} from "@/types";

import { SuppliersDataTable } from "./data-table";

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Proveedores",
    href: suppliersPath(),
  },
];

interface LocationOption {
  value: string;
  label: string;
}

interface Category {
  id: number;
  name: string;
}

interface Subcategory {
  id: number;
  name: string;
  category_id: number;
}

interface SuppliersProps {
  data: {
    suppliers: Supplier[];
    pagy: PagyMetadata;
  };
  filters: RansackFilters;
  cities: LocationOption[];
  states: LocationOption[];
  categories: Category[];
  subcategories: Subcategory[];
}

export default function Suppliers({
  data,
  filters,
  cities,
  states,
  categories,
  subcategories,
}: SuppliersProps) {
  const { suppliers, pagy } = data;
  
  // Use shared data table logic for pagination
  const { handleFilterChange } = useDataTable({
    filters,
    pagy,
    // Typed path via js-routes
    path: suppliersPath(),
  });

  const {
    value: nameSearch,
    setValue: setNameSearch,
    debouncedValue: debouncedName,
  } = useSyncedDebouncedValue(filters.name_cont ?? "");
  const {
    value: citySearch,
    setValue: setCitySearch,
    debouncedValue: debouncedCity,
  } = useSyncedDebouncedValue(filters.city_cont ?? "");
  const {
    value: stateSearch,
    setValue: setStateSearch,
    debouncedValue: debouncedState,
  } = useSyncedDebouncedValue(filters.state_cont ?? "");
  const [categoryFilter, setCategoryFilter] = useState(filters.category_id_eq ?? "");
  const [subcategoryFilter, setSubcategoryFilter] = useState(filters.subcategory_id_eq ?? "");

  // Sync local state with server filters on mount/update
  useEffect(() => {
    setCategoryFilter(filters.category_id_eq ?? "");
    setSubcategoryFilter(filters.subcategory_id_eq ?? "");
  }, [
    filters.category_id_eq,
    filters.subcategory_id_eq,
  ]);

  // Debounced name search
  useEffect(() => {
    if ((filters.name_cont ?? "") !== debouncedName) {
      handleFilterChange({ name_cont: debouncedName || undefined });
    }
    // We intentionally depend only on the debounced value so that
    // server-driven filter changes (e.g. clearing all filters) don't
    // re-trigger requests with stale values.
  }, [debouncedName]);

  // Debounced city search
  useEffect(() => {
    if ((filters.city_cont ?? "") !== debouncedCity) {
      handleFilterChange({ city_cont: debouncedCity || undefined });
    }
    // Same pattern: only react to local debounced changes.
  }, [debouncedCity]);

  // Debounced state search
  useEffect(() => {
    if ((filters.state_cont ?? "") !== debouncedState) {
      handleFilterChange({ state_cont: debouncedState || undefined });
    }
    // Same pattern: only react to local debounced changes.
  }, [debouncedState]);

  // Handle category filter change
  const handleCategoryChange = (value: string) => {
    const categoryId = value === "all" ? undefined : value;
    setCategoryFilter(value === "all" ? "" : value);
    setSubcategoryFilter(""); // Reset subcategory when category changes
    handleFilterChange({
      category_id_eq: categoryId,
      subcategory_id_eq: undefined, // Clear subcategory filter
    });
  };

  // Handle subcategory filter change
  const handleSubcategoryChange = (value: string) => {
    const subcategoryId = value === "all" ? undefined : value;
    setSubcategoryFilter(value === "all" ? "" : value);
    handleFilterChange({ subcategory_id_eq: subcategoryId });
  };

  const clearFilters = () => {
    setNameSearch("");
    setCategoryFilter("");
    setSubcategoryFilter("");
    setCitySearch("");
    setStateSearch("");
    router.get(
      window.location.pathname,
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["data", "filters"],
        reset: ["data"],
      },
    );
  };

  // Filter subcategories by selected category
  const availableSubcategories = categoryFilter 
    ? subcategories.filter(sub => sub.category_id === Number(categoryFilter))
    : [];
  
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Proveedores" />
      <div className="flex h-full flex-1 flex-col gap-4 p-4">
        <div className="flex flex-col space-y-2">
          <h2 className="text-2xl font-bold">Proveedores</h2>
          <p className="text-muted-foreground">
            Gestiona la información de proveedores y contactos
          </p>
        </div>


        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Total: {pagy.count} proveedores
          </div>
          <ButtonGroup>
            <Button variant="outline" asChild>
              <Link href={newSupplierPath()}>
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Proveedor
              </Link>
            </Button>
            <Button asChild>
              <Link href="/suppliers/import_new">Importar Excel</Link>
            </Button>
          </ButtonGroup>
        </div>

        {/* External Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <Input
            placeholder="Buscar por nombre..."
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
            className="max-w-sm"
          />

          <Select
            value={categoryFilter || "all"}
            onValueChange={handleCategoryChange}
          >
            <SelectTrigger className="max-w-sm">
              <SelectValue placeholder="Todas las categorías" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {categories.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={subcategoryFilter || "all"}
            onValueChange={handleSubcategoryChange}
            disabled={!categoryFilter}
          >
            <SelectTrigger className="max-w-sm">
              <SelectValue
                placeholder={
                  !categoryFilter
                    ? "Selecciona una categoría"
                    : "Todas las subcategorías"
                }
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las subcategorías</SelectItem>
              {availableSubcategories.map((subcategory) => (
                <SelectItem key={subcategory.id} value={String(subcategory.id)}>
                  {subcategory.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <SearchableSelect
            options={cities}
            value={citySearch}
            onValueChange={setCitySearch}
            placeholder="Seleccionar ciudad..."
            searchPlaceholder="Buscar ciudad..."
            emptyMessage="No se encontraron ciudades"
            className="max-w-sm"
          />

          <SearchableSelect
            options={states}
            value={stateSearch}
            onValueChange={setStateSearch}
            placeholder="Seleccionar provincia..."
            searchPlaceholder="Buscar provincia..."
            emptyMessage="No se encontraron provincias"
            className="max-w-sm"
          />

          {(nameSearch ||
            categoryFilter ||
            subcategoryFilter ||
            citySearch ||
            stateSearch) && (
            <Button variant="outline" onClick={clearFilters}>
              <Trash2 className="h-4 w-4" /> Limpiar filtros
            </Button>
          )}
        </div>

        <SuppliersDataTable
          suppliers={suppliers}
          pagy={pagy}
          filters={filters}
          cities={cities}
          states={states}
        />
      </div>
    </AppLayout>
  );
}
